import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

export default function BoostSettings() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  // Settings
  const [settings, setSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState({});
  const [settingsLoading, setSettingsLoading] = useState(true);

  // Platforms
  const [platforms, setPlatforms] = useState([]);
  const [platformsLoading, setPlatformsLoading] = useState(true);
  const [showPlatformModal, setShowPlatformModal] = useState(false);
  const [editPlatform, setEditPlatform] = useState(null);
  const [platForm, setPlatForm] = useState({ name: '', code: '', status: 'active', notes: '' });

  // Audit log
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditLastPage, setAuditLastPage] = useState(1);

  // Active tab
  const [tab, setTab] = useState('settings');

  const fetchSettings = () => {
    if (!companyId) return;
    setSettingsLoading(true);
    fetch(`${API}/companies/${companyId}/boosts/settings`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setSettings(d); setSettingsForm(d); })
      .catch(() => {})
      .finally(() => setSettingsLoading(false));
  };

  const saveSettings = () => {
    fetch(`${API}/companies/${companyId}/boosts/settings`, {
      method: 'PUT',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(settingsForm),
    })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setSettings(d); setSettingsForm(d); alert('Settings saved!'); })
      .catch(() => alert('Failed to save settings'));
  };

  const fetchPlatforms = () => {
    if (!companyId) return;
    setPlatformsLoading(true);
    fetch(`${API}/companies/${companyId}/boosts/platforms`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setPlatforms(Array.isArray(d) ? d : []))
      .catch(() => setPlatforms([]))
      .finally(() => setPlatformsLoading(false));
  };

  const fetchAudit = () => {
    if (!companyId) return;
    const params = new URLSearchParams({ page: auditPage, per_page: 20 });
    fetch(`${API}/companies/${companyId}/boosts/audit?${params}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => {
        setAuditLogs(Array.isArray(d) ? d : (d && Array.isArray(d.data) ? d.data : []));
        if (d && d.last_page) setAuditLastPage(d.last_page);
      })
      .catch(() => setAuditLogs([]));
  };

  useEffect(() => { fetchSettings(); fetchPlatforms(); }, [companyId]);
  useEffect(() => { if (tab === 'audit') fetchAudit(); }, [tab, auditPage, companyId]);

  // Platform CRUD
  const openCreatePlatform = () => {
    setEditPlatform(null);
    setPlatForm({ name: '', code: '', status: 'active', notes: '' });
    setShowPlatformModal(true);
  };

  const openEditPlatform = (p) => {
    setEditPlatform(p);
    setPlatForm({ name: p.name || '', code: p.code || '', status: p.status || 'active', notes: p.notes || '' });
    setShowPlatformModal(true);
  };

  const savePlatform = () => {
    const url = editPlatform
      ? `${API}/companies/${companyId}/boosts/platforms/${editPlatform.id}`
      : `${API}/companies/${companyId}/boosts/platforms`;
    const method = editPlatform ? 'PUT' : 'POST';

    fetch(url, {
      method,
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(platForm),
    })
      .then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.message || 'Error'); }); return r.json(); })
      .then(() => { setShowPlatformModal(false); fetchPlatforms(); })
      .catch(e => alert(e.message));
  };

  const deletePlatform = (p) => {
    if (!window.confirm(`Delete platform "${p.name}"?`)) return;
    fetch(`${API}/companies/${companyId}/boosts/platforms/${p.id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    })
      .then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.message || 'Error'); }); return r.json(); })
      .then(() => fetchPlatforms())
      .catch(e => alert(e.message));
  };

  if (!companyId) return <div style={pageStyle}><p>Please select a company first.</p></div>;

  return (
    <div style={pageStyle}>
      <h1 style={{ margin: '0 0 20px', fontSize: '1.5rem', color: '#1e293b' }}>⚙️ Boost Settings</h1>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid #e2e8f0' }}>
        {['settings', 'platforms', 'audit'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '10px 22px', background: 'none', border: 'none', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
            color: tab === t ? '#3b82f6' : '#64748b',
            borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
            marginBottom: -2,
          }}>
            {t === 'settings' ? 'General Settings' : t === 'platforms' ? 'Platforms' : 'Audit Log'}
          </button>
        ))}
      </div>

      {/* Settings Tab */}
      {tab === 'settings' && (
        <div style={cardStyle}>
          {settingsLoading ? <p>Loading...</p> : (
            <>
              <div style={formGrid}>
                <label style={labelStyle}>
                  Default Expense Category
                  <input value={settingsForm.default_expense_category || ''} onChange={e => setSettingsForm({ ...settingsForm, default_expense_category: e.target.value })} style={inputStyle} />
                </label>
                <label style={labelStyle}>
                  Ending Soon (days)
                  <input type="number" min="1" value={settingsForm.ending_soon_days || ''} onChange={e => setSettingsForm({ ...settingsForm, ending_soon_days: e.target.value })} style={inputStyle} />
                </label>
              </div>

              <h3 style={{ margin: '20px 0 12px', fontSize: '0.95rem', color: '#334155' }}>Notifications</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.88rem', color: '#475569', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!settingsForm.notify_boost_starting} onChange={e => setSettingsForm({ ...settingsForm, notify_boost_starting: e.target.checked })} />
                  Notify when boost starting
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.88rem', color: '#475569', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!settingsForm.notify_boost_ending} onChange={e => setSettingsForm({ ...settingsForm, notify_boost_ending: e.target.checked })} />
                  Notify when boost ending soon
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.88rem', color: '#475569', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!settingsForm.notify_past_end_date} onChange={e => setSettingsForm({ ...settingsForm, notify_past_end_date: e.target.checked })} />
                  Notify when boost past end date
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.88rem', color: '#475569', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!settingsForm.notify_unpaid} onChange={e => setSettingsForm({ ...settingsForm, notify_unpaid: e.target.checked })} />
                  Notify on unpaid boosts
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.88rem', color: '#475569', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!settingsForm.notify_partial_paid} onChange={e => setSettingsForm({ ...settingsForm, notify_partial_paid: e.target.checked })} />
                  Notify on partially paid boosts
                </label>
              </div>

              <div style={{ marginTop: 24 }}>
                <button onClick={saveSettings} style={primaryBtnStyle}>Save Settings</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Platforms Tab */}
      {tab === 'platforms' && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: '1rem', color: '#1e293b' }}>Boost Platforms</h3>
            <button onClick={openCreatePlatform} style={primaryBtnStyle}>+ Add Platform</button>
          </div>
          {platformsLoading ? <p>Loading...</p> : (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Code</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Entries</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {platforms.length === 0 ? (
                  <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No platforms configured</td></tr>
                ) : platforms.map(p => (
                  <tr key={p.id}>
                    <td style={tdStyle}>{p.name}</td>
                    <td style={tdStyle}>{p.code || '—'}</td>
                    <td style={tdStyle}>
                      <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 600, color: '#fff', background: p.status === 'active' ? '#22c55e' : '#94a3b8' }}>
                        {p.status}
                      </span>
                    </td>
                    <td style={tdStyle}>{p.entries_count ?? 0}</td>
                    <td style={tdStyle}>
                      <button onClick={() => openEditPlatform(p)} style={smallBtnStyle}>✏️</button>
                      <button onClick={() => deletePlatform(p)} style={{ ...smallBtnStyle, color: '#ef4444' }}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Audit Log Tab */}
      {tab === 'audit' && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 14px', fontSize: '1rem', color: '#1e293b' }}>Audit Log</h3>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Action</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Old Value</th>
                <th style={thStyle}>New Value</th>
                <th style={thStyle}>User</th>
                <th style={thStyle}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.length === 0 ? (
                <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No audit logs</td></tr>
              ) : auditLogs.map((log, i) => (
                <tr key={i}>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontSize: '0.78rem' }}>{log.created_at ? new Date(log.created_at).toLocaleString() : '—'}</td>
                  <td style={tdStyle}><span style={{ padding: '2px 8px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 600, color: '#fff', background: actionColor(log.action) }}>{(log.action || '').replace(/_/g, ' ')}</span></td>
                  <td style={tdStyle}>{log.auditable_type || '—'}</td>
                  <td style={{ ...tdStyle, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.old_value || '—'}</td>
                  <td style={{ ...tdStyle, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.new_value || '—'}</td>
                  <td style={tdStyle}>{log.user ? log.user.name : '—'}</td>
                  <td style={tdStyle}>{log.reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {auditLastPage > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <button disabled={auditPage <= 1} onClick={() => setAuditPage(auditPage - 1)} style={pageBtnStyle}>← Prev</button>
              <span style={{ padding: '6px 12px', fontSize: '0.85rem', color: '#475569' }}>Page {auditPage} of {auditLastPage}</span>
              <button disabled={auditPage >= auditLastPage} onClick={() => setAuditPage(auditPage + 1)} style={pageBtnStyle}>Next →</button>
            </div>
          )}
        </div>
      )}

      {/* Platform Create/Edit Modal */}
      {showPlatformModal && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxWidth: 440 }}>
            <h2 style={{ margin: '0 0 16px', fontSize: '1.1rem', color: '#1e293b' }}>{editPlatform ? 'Edit Platform' : 'New Platform'}</h2>
            <label style={labelStyle}>
              Name *
              <input value={platForm.name} onChange={e => setPlatForm({ ...platForm, name: e.target.value })} style={inputStyle} />
            </label>
            <label style={{ ...labelStyle, marginTop: 10 }}>
              Code
              <input value={platForm.code} onChange={e => setPlatForm({ ...platForm, code: e.target.value })} placeholder="e.g. FB, GOOG" style={inputStyle} />
            </label>
            <label style={{ ...labelStyle, marginTop: 10 }}>
              Status
              <select value={platForm.status} onChange={e => setPlatForm({ ...platForm, status: e.target.value })} style={{ ...selectStyle, display: 'block', width: '100%', marginTop: 4 }}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
            <label style={{ ...labelStyle, marginTop: 10 }}>
              Notes
              <textarea value={platForm.notes} onChange={e => setPlatForm({ ...platForm, notes: e.target.value })} style={{ ...inputStyle, minHeight: 50 }} />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
              <button onClick={() => setShowPlatformModal(false)} style={cancelBtnStyle}>Cancel</button>
              <button onClick={savePlatform} style={primaryBtnStyle}>{editPlatform ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const actionColor = (action) => {
  if (!action) return '#94a3b8';
  if (action.includes('created')) return '#3b82f6';
  if (action.includes('changed') || action.includes('updated') || action.includes('renamed')) return '#f59e0b';
  if (action.includes('deleted') || action.includes('archived')) return '#ef4444';
  return '#8b5cf6';
};

const pageStyle = { padding: 28, maxWidth: 1000, margin: '0 auto' };
const cardStyle = { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const thStyle = { textAlign: 'left', padding: '8px 10px', fontSize: '0.75rem', color: '#64748b', borderBottom: '2px solid #e2e8f0', fontWeight: 600, textTransform: 'uppercase' };
const tdStyle = { padding: '8px 10px', fontSize: '0.85rem', color: '#334155', borderBottom: '1px solid #f1f5f9' };
const inputStyle = { display: 'block', width: '100%', padding: '7px 10px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: '0.88rem', marginTop: 4, boxSizing: 'border-box' };
const selectStyle = { padding: '7px 10px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: '0.88rem', background: '#fff', cursor: 'pointer' };
const primaryBtnStyle = { padding: '8px 18px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 7, fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer' };
const cancelBtnStyle = { padding: '8px 18px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 7, fontSize: '0.88rem', cursor: 'pointer' };
const smallBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: '0.92rem' };
const pageBtnStyle = { padding: '6px 14px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', fontSize: '0.82rem', cursor: 'pointer', color: '#475569' };
const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle = { background: '#fff', borderRadius: 12, padding: 28, width: '95%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' };
const formGrid = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px 16px' };
const labelStyle = { fontSize: '0.82rem', color: '#475569', fontWeight: 500, display: 'block' };
